import React, { useContext, useEffect, useState } from 'react';
import Typography from '@mui/material/Typography';
import classNames from 'classnames';
import { useI18nContext, Button } from '@procore/core-react';
import { type Project } from '../../types/project';
import { useStyles } from './PublicProjectContactInfo.styles';
import AppContext from '../AppContext/AppContext';
import getContactFormPersona from '../../services/contact-form-persona';
import getProjectCommunicationStatus from '../../data/project/project-communication-status';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';
import BusinessLogo from '../Business/BusinessLogo';
import { formatDate } from '../../helpers/format-datetime';
import { useLoginUrl } from '../../helpers/useLoginUrl';
import { getBidDueStatus } from '../../helpers/build-texts';
import { getNdaProjectDisplay } from '../../helpers/nda-display';

const PublicProjectContactInfo = ({ project }: { project: Project }) => {
  const I18n = useI18nContext();
  const { isUserLoggedIn, loginInformationId } = useContext(AppContext);
  const [getInfoInProgress, setGetInfoInProgress] = useState(true);
  const [bidCommunicationExists, setBidCommunciationExists] = useState(false);
  const [buttonTexti18nKey, setButtonTexti18nKey] = useState('');
  const [requestSubmittedBy, setRequestSubmittedBy] = useState('');
  const [requestSubmittedAt, setRequestSubmittedAt] = useState('');
  const classes = useStyles();
  const loginUrl = useLoginUrl();

  const ndaDisplay = getNdaProjectDisplay(project);
  const { biddingClosed } = getBidDueStatus(project);
  const link = !isUserLoggedIn
    ? loginUrl
    : '/contact-business/' + project.bidPackagePrimarySlug;

  const setUserPersona = async () => {
    const persona = await getContactFormPersona();
    const status = await getProjectCommunicationStatus(
      project.companyId,
      project.bidPackageId,
      loginInformationId,
      persona.companyId
    );
    if (status && status.data) {
      if (status.data.communication_exists) {
        setButtonTexti18nKey(
          'pages.j.public_project_layout.public_project_contact_info.communication_exists'
        );
      }
      setBidCommunciationExists(status.data.communication_exists);
      if (status.data.communication_exists) {
        setRequestSubmittedBy(status.data.submitted_by);
        setRequestSubmittedAt(status.data.submitted_at);
      }
    }
    setGetInfoInProgress(false);
  };

  useEffect(() => {
    const textKey = !isUserLoggedIn
      ? 'pages.j.public_project_layout.public_project_contact_info.login_to_contact'
      : 'pages.j.public_project_layout.public_project_contact_info.contact_about_project';
    setButtonTexti18nKey(textKey);
    if (isUserLoggedIn) {
      setUserPersona();
    } else if (isUserLoggedIn === false) {
      setGetInfoInProgress(false);
    }
  }, [isUserLoggedIn]);

  return (
    <div
      className={classNames(
        classes.container,
        ndaDisplay.isNda ? classes.isNdaProject : ''
      )}
      data-test-id="project-contact-info"
    >
      <Typography variant="h2" className={classes.title}>
        {I18n.t(
          'pages.j.public_project_layout.public_project_contact_info.contact_info'
        )}
      </Typography>
      <div className={classes.getInTouch}>
        {I18n.t(
          'pages.j.public_project_layout.public_project_contact_info.get_in_touch'
        )}
      </div>
      <div className={classes.businessContainer}>
        <div className={classes.businessLogo}>
          <BusinessLogo logoUrl={project.businessProfileLogoUrl} size="sm" />
        </div>
        <div className={classes.businessNameAndLink}>
          <Typography variant="h3" className={classes.businessName}>
            {project.companyName}
          </Typography>
          {project.businessProfileSlug && (
            <AnalyticsWrappedComponent
              componentType="a"
              eventText="Clicked Contact About Project From Public Project Page"
              eventLabel={`${project.businessProfileSlug}, CTA, View Profile`}
              aria-label="View Profile"
              className={classes.businessLink}
              data-track-click={`${project.bidPackagePrimarySlug}, CTA, View Profile`}
              href={'/p/' + project.businessProfileSlug}
              target="_blank"
              rel="noreferrer"
              tabIndex={-1}
              additionalEventProperties={{
                project_id: project.bidPackageId,
                slug: project.bidPackagePrimarySlug,
                /*solicitor_id: project.companyId,*/
                cta_source: 'view_profile',
              }}
            >
              {I18n.t(
                'pages.j.public_project_layout.public_project_contact_info.view_profile'
              )}
            </AnalyticsWrappedComponent>
          )}
        </div>
      </div>
      {!getInfoInProgress && (
        <AnalyticsWrappedComponent
          componentType="a"
          eventText="Clicked Contact About Project From Public Project Page"
          eventLabel={`${project.bidPackagePrimarySlug}, CTA, Contact About Project`}
          aria-label="Contact About Project"
          className={classNames(
            classes.buttonContainer,
            biddingClosed || bidCommunicationExists
              ? classes.disableButtonContainer
              : ''
          )}
          data-track-click={`${project.bidPackagePrimarySlug}, CTA, Contact About Project`}
          href={link}
          target="_blank"
          rel="noreferrer"
          tabIndex={-1}
          additionalEventProperties={{
            project_id: project.bidPackageId,
            slug: project.bidPackagePrimarySlug,
            solicitor_id: project.companyId,
            cta_source: 'contact_solicitor',
          }}
        >
          <Button
            className={classNames(classes.button, 'A11yException')}
            variant="primary"
            disabled={biddingClosed || bidCommunicationExists}
          >
            {I18n.t(buttonTexti18nKey)}
          </Button>
        </AnalyticsWrappedComponent>
      )}
      {biddingClosed && (
        <div
          className={classes.biddingClosed}
          data-test-id="bidding-closed-notice"
        >
          {I18n.t(
            'pages.j.public_project_layout.public_project_contact_info.bidding_has_closed'
          )}
        </div>
      )}
      {bidCommunicationExists && requestSubmittedBy && requestSubmittedAt && (
        <div className={classes.biddingClosed}>
          {I18n.t(
            'pages.j.public_project_layout.public_project_contact_info.request_submitted',
            {
              name: requestSubmittedBy,
              date: formatDate(requestSubmittedAt),
            }
          )}
        </div>
      )}
    </div>
  );
};

export default PublicProjectContactInfo;
